import between from '@inquisitive/between';

import {
  LearningSequenceItemTypes,
  LEARNING_SEQUENCE_CATEGORY_ANSWER,
  LEARNING_SEQUENCE_CATEGORY_EXPLANATION,
  LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES,
  REGION_US,
  TEACHING_LEARNING_SEQUENCE_THE_E_TYPES,
  LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_CATEGORY_ACTIVITY_DESCRIPTION,
} from 'global/constants';
import {
  LessonTeachingLearningSequenceFragment_teachingAndLearningSequence,
  LessonTeachingLearningSequenceFragment_teachingAndLearningSequence_data,
} from 'approot/lesson/sections/teaching-learning-sequence/__generated__/LessonTeachingLearningSequenceFragment';
import { Regions } from 'approot/shared/region/region.constants';
import { TeachingAndLearningSequence } from './teaching-learning-sequence.constants';
import { JSONObject } from 'global/graphql/scalars.type';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import {
  GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence,
  GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence_theEsDetails,
} from 'approot/activity-builder/components/teaching-notes/__generated__/GetLessonTeachingAndLearningSequence';

export function getNoteSort(lastNoteSort?: string | null) {
  return lastNoteSort
    ? between.between(lastNoteSort, between.highest)
    : between.between(between.lowest, between.highest);
}

export function getQuestionNumberWithSpans(
  questions: { span: number | null }[],
  index: number
) {
  const numbers = questions.reduce((accum, curr, index) => {
    if (index === 0) {
      accum.push({
        start: 1,
        end: curr.span ? curr.span : 1,
      });
      return accum;
    }

    const nextNumber = accum[index - 1].end + 1;
    accum.push({
      start: nextNumber,
      end: curr.span ? nextNumber + (curr.span - 1) : nextNumber,
    });

    return accum;
  }, [] as { start: number; end: number }[]);

  return numbers[index];
}

export function getNotes({
  itemType,
  sequences,
  questionGroupId = null,
  sectionId = null,
}: {
  itemType: LearningSequenceItemTypes;
  sequences?: TeachingAndLearningSequence[] | null;
  questionGroupId?: number | null;
  sectionId?: string | null;
}) {
  return sequences?.filter(
    s =>
      s.type === itemType &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_ANSWER &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_EXPLANATION &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_ACTIVITY_DESCRIPTION &&
      ('questionGroupId' in s ? s.questionGroupId === questionGroupId : true) &&
      ('component' in s && s.component
        ? s.component.sectionId === sectionId
        : true)
  );
}

export function getStaticNotes(
  itemType: LearningSequenceItemTypes,
  sequences?:
    | LessonTeachingLearningSequenceFragment_teachingAndLearningSequence[]
    | null,
  questionGroupId: number | null = null
) {
  return sequences?.filter(
    s =>
      s.type === itemType &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_ANSWER &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_EXPLANATION &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      s.questionGroupId === questionGroupId
  );
}

export function getQuestionAnswer(
  sequences?: TeachingAndLearningSequence[] | null,
  questionGroupId: number | null = null,
  sectionId?: string | null
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_ANSWER &&
      ('questionGroupId' in s ? s.questionGroupId === questionGroupId : true) &&
      ('component' in s && s.component
        ? s.component.sectionId === sectionId
        : true)
  );
}

export function getStaticQuestionAnswer(
  sequences?:
    | LessonTeachingLearningSequenceFragment_teachingAndLearningSequence[]
    | null,
  questionGroupId: number | null = null
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_ANSWER &&
      s.questionGroupId === questionGroupId
  );
}

export function getQuestionLevelOfAttainment(
  sequences?: TeachingAndLearningSequence[] | null,
  questionGroupId: number | null = null,
  sectionId?: string | null
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      ('questionGroupId' in s ? s.questionGroupId === questionGroupId : true) &&
      ('component' in s && s.component
        ? s.component.sectionId === sectionId
        : true)
  );
}

export function getActivityMaterials(
  sequences:
    | GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence[]
    | null,
  sectionId: string | null | undefined
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED &&
      s.component?.sectionId === sectionId
  );
}

export function getStaticQuestionLevelOfAttainment(
  sequences?:
    | LessonTeachingLearningSequenceFragment_teachingAndLearningSequence[]
    | null,
  questionGroupId: number | null = null
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      s.questionGroupId === questionGroupId
  );
}

export function hasSequenceContent(
  sequences?:
    | GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence[]
    | TeachingAndLearningSequence[]
    | null
) {
  if (
    !sequences ||
    sequences.length === 0 ||
    sequences.every(
      s =>
        isRichtextEmpty(getRichtextContent(s.data)) &&
        !s.theEsDetails?.length &&
        !(s as GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence)
          .highImpactTeachingStrategiesDetails?.length &&
        !(s as GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence)
          .visibleThinkingActivitiesDetails?.length &&
        !(s as GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence)
          .component?.properties.activitySettings.duration
    )
  ) {
    return false;
  }

  return true;
}

export function sortTheEs(
  theEsDetails:
    | GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence_theEsDetails[]
    | null
) {
  const orderMap = new Map(
    TEACHING_LEARNING_SEQUENCE_THE_E_TYPES.map((type, index) => [type, index])
  );

  return (
    theEsDetails?.slice().sort((a, b) => {
      const aIndex = orderMap.get(a.id) || 0;
      const bIndex = orderMap.get(b.id) || 0;
      return aIndex - bIndex;
    }) || []
  );
}

export function getCategoryOptions({
  region,
  categories,
}: {
  region: Regions;
  categories:
    | typeof LEARNING_SEQUENCE_QUESTION_CATEGORIES
    | typeof LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB;
}) {
  const options = [...categories];
  if (region !== REGION_US) {
    return options.filter(
      o => o.value !== LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS
    );
  }
  return options;
}

export function getRichtextContent(
  data:
    | LessonTeachingLearningSequenceFragment_teachingAndLearningSequence_data
    | JSONObject
    | undefined
) {
  if (!data) return;
  return isStaticContent(data) ? data.richtext : data;
}

export function isStaticContent(
  data:
    | LessonTeachingLearningSequenceFragment_teachingAndLearningSequence_data
    | JSONObject
    | undefined
) {
  if (!data) return;
  return 'richtext' in data;
}
